<script lang="ts">
  import { costsApi, type CostBreakdown, ApiError } from '../lib/api';
  import { liveCosts } from '../lib/live-costs.svelte';
  import { CostsPeriod } from '../lib/costs-period';
  import { CostsTab } from '../lib/costs-tab';
  import { CostsFilter } from '../lib/costs-filter';
  import { PeriodTotals } from '../lib/period-totals';
  import { CostRoute } from '../lib/cost-route';
  import type { CostRow } from '../lib/cost-row';
  import { Format } from '../lib/format';
  import { navigation } from '../lib/navigation.svelte';
  import CostTotals from '../components/CostTotals.svelte';
  import CostTable from '../components/CostTable.svelte';
  import CostFilters from '../components/CostFilters.svelte';
  import CostSkeleton from '../components/CostSkeleton.svelte';
  import PeriodPicker from '../components/PeriodPicker.svelte';
  import CostDetail from './CostDetail.svelte';

  interface Props {
    onUnauthorized: () => void;
    /** O topo mostra quando o número foi buscado e se a tela recebe gasto novo
     *  sozinha — os dois só valem a verdade se vierem de quem busca. */
    onRefreshed?: (at: Date, live: boolean) => void;
  }

  let { onUnauthorized, onRefreshed }: Props = $props();

  let data = $state<CostBreakdown | null>(null);
  // Dois estados diferentes de propósito: a PRIMEIRA carga não tem o que mostrar
  // e ganha esqueleto; a recarga mantém a tabela em tela e só esmaece. Voltar ao
  // esqueleto a cada troca de período apagava o número que se estava lendo.
  let refetching = $state(false);
  let error = $state<string | null>(null);
  let live = $state(false);
  let lastFetchedAt: Date | null = null;

  const tab = $derived(CostsTab.fromQuery(navigation.valueOf('aba')));
  const period = $derived(CostsPeriod.fromQuery(navigation.valueOf('de'), navigation.valueOf('ate')));
  const route = $derived(CostRoute.fromPath(navigation.path));
  const filter = $derived(CostsFilter.fromQuery((key) => navigation.valueOf(key)));

  const allRows = $derived(data ? tab.rowsOf(data) : []);
  const rows = $derived(filter.apply(allRows));

  // Os cartões somam sempre as linhas POR CLIENTE, não as da aba aberta: a visão
  // por modelo junta origens diferentes na mesma linha e não sabe separar teste
  // de atendimento. Assim o número grande obedece ao recorte em qualquer aba.
  const totals = $derived(
    PeriodTotals.of(data ? filter.apply(CostsTab.CLIENT.rowsOf(data)) : []),
  );

  // A lista não é buscada enquanto o detalhe está aberto: voltar da tela de
  // detalhe encontra a tabela já pronta, sem piscar o esqueleto de novo.
  $effect(() => {
    if (route.showsDetail) return;
    void load(period);
  });

  liveCosts({
    period: () => period,
    enabled: () => !route.showsDetail,
    reload: () => void load(period, { background: true }),
    onLive: (connected) => {
      live = connected;
      onRefreshed?.(lastFetchedAt ?? new Date(), connected);
    },
  });

  function open(row: CostRow): void {
    const alvo = row.detailRoute;
    if (!alvo) return;
    navigation.goTo(CostRoute.of(alvo.scope, alvo.id).path);
  }

  /**
   * `background` é a recarga que o aviso do servidor dispara. Ela não esmaece nem
   * troca a tabela por erro: o usuário não pediu nada, e o fingerprint do dia muda
   * quase todo minuto num dia movimentado — piscar a 45% nessa cadência apaga o
   * número que se estava lendo. Falha de fundo fica com o dado velho em tela; quem
   * conta a verdade da conexão é o selo AO VIVO.
   */
  async function load(range: CostsPeriod, { background = false } = {}): Promise<void> {
    if (!background) {
      refetching = true;
      error = null;
    }
    try {
      data = await costsApi.breakdown(range.from, range.to);
      lastFetchedAt = new Date();
      onRefreshed?.(lastFetchedAt, live);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      if (!background) error = err instanceof Error ? err.message : 'erro ao carregar';
    } finally {
      refetching = false;
    }
  }
</script>

<div class="costs">
  <div class="toolbar">
    <PeriodPicker
      applied={period}
      onApply={(escolhido) => navigation.merge({ de: escolhido.from, ate: escolhido.to })}
    />
  </div>

  {#if route.showsDetail}
    <CostDetail
      scope={route.scope!}
      id={route.id!}
      {period}
      {onUnauthorized}
      onLive={(connected) => {
        live = connected;
        onRefreshed?.(lastFetchedAt ?? new Date(), connected);
      }}
      onBack={() => navigation.goTo(CostRoute.list().path)}
      onOpenWorkflow={(id) => navigation.goTo(CostRoute.of('workflow', id).path)}
    />
  {:else if error}
    <p class="state state-error" role="alert">{error}</p>
  {:else if !data}
    <CostSkeleton />
  {:else}
    <div class="content" class:refetching>
      <CostTotals total={totals} />

      {#if totals.unpricedCalls > 0}
        <p class="warn mono">
          {Format.count(totals.unpricedCalls)} chamada(s) de modelo sem tabela de preço — não
          somadas ao custo.
        </p>
      {/if}

      <!-- Botão com `aria-pressed`, não `role="tab"`: o papel de aba promete
           navegação por setas, e aqui a troca é por Tab e Enter. -->
      <div class="tabs">
        {#each CostsTab.all() as option (option.slug)}
          <button
            class="tab"
            aria-pressed={option.is(tab)}
            class:active={option.is(tab)}
            onclick={() => navigation.merge({ aba: option.slug })}
          >
            {option.label}
          </button>
        {/each}
      </div>

      <CostFilters
        {filter}
        shown={rows.length}
        total={allRows.length}
        onChange={(patch) => navigation.merge(patch)}
      />

      <CostTable
        firstColumn={tab.firstColumn}
        {rows}
        {filter}
        onSort={(patch) => navigation.merge(patch)}
        onOpen={open}
        onClearFilters={() => navigation.merge(CostsFilter.cleared())}
      />
    </div>
  {/if}
</div>

<style>
  .costs {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  .toolbar {
    display: flex;
    justify-content: flex-end;
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    transition: opacity var(--duration-normal, 200ms) var(--easing-default);
  }
  /* Recarga não apaga a tela: o dado antigo fica legível enquanto o novo vem. */
  .content.refetching {
    opacity: 0.45;
  }

  .warn {
    font-size: 12px;
    color: var(--color-warning, var(--text-tertiary));
  }

  .tabs {
    display: flex;
    gap: var(--space-2);
    border-bottom: 1px solid var(--border-subtle);
  }
  .tab {
    padding: var(--space-2) var(--space-3);
    font-size: 13px;
    color: var(--text-tertiary);
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    background: none;
    border-top: none;
    border-left: none;
    border-right: none;
    transition: color var(--duration-fast) var(--easing-default);
  }
  .tab:hover {
    color: var(--text-secondary);
  }
  .tab:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: -2px;
  }
  .tab.active {
    color: var(--text-primary);
    border-bottom-color: var(--color-success);
  }

  .state {
    color: var(--text-tertiary);
    text-align: center;
    margin: var(--space-7) 0;
  }
  .state-error {
    color: var(--color-danger);
  }
</style>
